import { useEffect, useState, useRef } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ImagePlus, Trash2, Lock, Sparkles, Loader2, Heart } from "lucide-react";
import { Link } from "react-router-dom";

const TABS = [
  { v: "all", l: "All" },
  { v: "training", l: "Training" },
  { v: "games", l: "Games" },
  { v: "tournaments", l: "Tournaments" },
];

const AuthImg = ({ teamId, photoId, alt }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url;
    let active = true;
    api.get(`/teams/${teamId}/photos/${photoId}/file`, { responseType: "blob" })
      .then((r) => { if (!active) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => {});
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [teamId, photoId]);
  return src
    ? <img src={src} alt={alt || ""} className="w-full h-full object-cover" />
    : <div className="w-full h-full bg-accent animate-pulse" />;
};

export const TeamPhotos = ({ teamId, canUpload = false, showUpsell = false }) => {
  const [tab, setTab] = useState("all");
  const [photos, setPhotos] = useState(null);
  const [locked, setLocked] = useState(false);
  const [album, setAlbum] = useState("training");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    let active = true;
    setPhotos(null);
    api.get(`/teams/${teamId}/photos${tab !== "all" ? `?album=${tab}` : ""}`)
      .then((r) => { if (active) { setPhotos(r.data); setLocked(false); } })
      .catch((e) => {
        if (!active) return;
        if (e?.response?.status === 403) { setLocked(true); setPhotos([]); }
        else setPhotos([]);
      });
    return () => { active = false; };
  }, [teamId, tab]);

  const reload = () => {
    api.get(`/teams/${teamId}/photos${tab !== "all" ? `?album=${tab}` : ""}`)
      .then((r) => setPhotos(r.data)).catch(() => {});
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10MB"); e.target.value = ""; return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("album", album);
    fd.append("caption", caption);
    setBusy(true);
    try {
      await api.post(`/teams/${teamId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo shared");
      setCaption("");
      if (tab === "all" || tab === album) reload(); else setTab(album);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/teams/${teamId}/photos/${id}`);
      setPhotos((l) => l.filter((p) => p.id !== id));
    } catch (e) { toast.error(apiErr(e)); }
  };

  const react = async (p) => {
    try {
      const { data } = await api.post(`/teams/${teamId}/photos/${p.id}/react`);
      setPhotos((l) => l.map((x) => x.id === p.id ? { ...x, reacted: data.reacted, reaction_count: data.count } : x));
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (locked) {
    if (!showUpsell) return null;
    return (
      <div className="flex flex-col items-center justify-center text-center py-10 px-6 rounded-2xl border border-dashed border-border bg-card" data-testid="team-photos-locked">
        <div className="h-12 w-12 rounded-2xl brand-gradient flex items-center justify-center text-white mb-4"><Lock size={20} /></div>
        <h3 className="font-heading font-bold text-lg">Team photo sharing is a Club plan feature</h3>
        <p className="text-muted-foreground mt-1.5 max-w-md text-sm">Upgrade to the Club plan so your teams can share training, game and tournament photos.</p>
        <Button asChild className="mt-5 brand-gradient text-white border-0 hover:opacity-90" data-testid="team-photos-upgrade-btn">
          <Link to="/app/payments"><Sparkles size={16} className="mr-2" /> Upgrade to Club</Link>
        </Button>
      </div>
    );
  }

  return (
    <div data-testid={`team-photos-${teamId}`}>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {TABS.map((t) => (
          <button key={t.v} onClick={() => setTab(t.v)}
            className={`text-sm px-3 py-1.5 rounded-full transition-colors ${tab === t.v ? "brand-gradient text-white" : "bg-accent text-muted-foreground hover:text-foreground"}`}
            data-testid={`photos-tab-${t.v}`}>{t.l}</button>
        ))}
      </div>

      {canUpload && (
        <div className="grid sm:grid-cols-[auto_1fr_auto] gap-3 items-end mb-5 p-4 rounded-xl border border-border bg-background/50">
          <div>
            <Label className="text-xs">Album</Label>
            <Select value={album} onValueChange={setAlbum}>
              <SelectTrigger className="mt-1.5 w-36" data-testid="photo-album-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="training">Training</SelectItem>
                <SelectItem value="games">Games</SelectItem>
                <SelectItem value="tournaments">Tournaments</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Caption (optional)</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. Saturday scrimmage" className="mt-1.5" data-testid="photo-caption-input" />
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" id={`photo-file-${teamId}`} data-testid="photo-upload-input" />
            <Button asChild disabled={busy} className="brand-gradient text-white border-0 hover:opacity-90">
              <label htmlFor={`photo-file-${teamId}`} className="cursor-pointer">
                {busy ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <ImagePlus size={16} className="mr-1.5" />}
                {busy ? "Uploading…" : "Add photo"}
              </label>
            </Button>
          </div>
        </div>
      )}

      {photos === null ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-muted-foreground" /></div>
      ) : photos.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10" data-testid="team-photos-empty">No photos yet. {canUpload ? "Share the first one!" : ""}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => (
            <figure key={p.id} className="relative group rounded-xl overflow-hidden border border-border bg-accent aspect-square" data-testid={`team-photo-${p.id}`}>
              <AuthImg teamId={teamId} photoId={p.id} alt={p.caption} />
              {p.caption && (
                <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs p-2 pt-6 pl-14 line-clamp-2">{p.caption}</figcaption>
              )}
              <button onClick={() => react(p)}
                className={`absolute bottom-2 left-2 z-10 inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium backdrop-blur transition-colors ${p.reacted ? "bg-rose-500/90 text-white" : "bg-black/50 text-white hover:bg-black/70"}`}
                data-testid={`react-photo-${p.id}`}>
                <Heart size={13} className={p.reacted ? "fill-current" : ""} /> {p.reaction_count > 0 ? p.reaction_count : ""}
              </button>
              <button onClick={() => del(p.id)} className="absolute top-2 right-2 bg-black/60 text-white rounded-full h-7 w-7 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`del-photo-${p.id}`}>
                <Trash2 size={14} />
              </button>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
};
